import type {
  DomainGroup,
  ModelMapResponse,
  NodeKey,
  NodeKind,
  NodeUnit,
  SlotInputNode,
} from 'types/packages-api/slot-valuation-inputs';
import {
  EMPTY,
  formatNodeDisplay,
  formatNodeValue,
  isUnappliedCategoryRate,
} from './utils';

/**
 * AC 6's cap: at most four value lines beneath a function node.
 *
 * ── THIS IS *NOT* `node_binder.rb`'s `MAX_NODE_VALUES`, AND THE DIFFERENCE MATTERS ────
 *
 * That constant (also 4) truncates ONE node's MONTHLY SERIES PREVIEW — `series_for`,
 * `node_binder.rb:104`. A scalar node carries 0 or 1 entries, never more, and that
 * cardinality is asserted over the whole registry (`node_binder.rb:60-61`).
 *
 * AC 6 is a different quantity: the number of DISTINCT INPUT NODES whose values are listed
 * under a function node. Nothing server-side computes that list — the payload ships every
 * input key on every node and lets the renderer join — and 11 of the 26 function nodes have
 * MORE than four inputs (`decline` 20, `afe_cost` 11, `discount` 10, `revenue` 9, `tax` 8),
 * so the cap is load-bearing rather than theoretical. It is applied here, once, and the
 * table view is free to import it if it ever renders AC 6 too.
 */
export const MAX_VALUE_LINES = 4;

/**
 * The "overlong" threshold, in characters.
 *
 * The node's TextBlock is capped at `NODE_MAX_WIDTH` (150px) with `WrapFit`, so a long line
 * can never WIDEN a node — GoJS wraps it instead. AC 8's width clause therefore holds by
 * construction and this cap is not what enforces it; what it bounds is HEIGHT.
 *
 * MEASURED, NOT ASSUMED. At the value-line font (10px IBM Plex Sans, ~5.2px average advance)
 * 150px is reached around 29 characters, and the longest line the five reference slots
 * actually produce is 29 ("Net Revenue Interest: 0.1481%"). So a line at this cap wraps to
 * AT MOST TWO rows, and four lines can occupy at most eight — bounded, and the layout gives
 * a taller node more room rather than overlapping it.
 *
 * A tighter cap was rejected: 26 would drop that NRI line, "LTD Start Offset (months): 9"
 * and "Discount To Date: 09/01/2026", all of which are exactly the values AC 6 asks for.
 * Nothing on live data is dropped by this cap today — it is the guard for a label longer
 * than any the registry currently declares, so it is exercised by test rather than by data.
 */
export const MAX_VALUE_LINE_CHARS = 30;

/** Transient click-highlight dimming (existing behaviour). */
export const DIMMED_NODE_OPACITY = 0.15;
/** Transient click-highlight dimming, links (existing behaviour). */
export const DIMMED_LINK_OPACITY = 0.06;
/**
 * AC 4's faded option group, in THIS view only.
 *
 * ── THE TWO VIEWS DELIBERATELY DIVERGE NOW, AND THIS COMMENT USED TO DENY IT ────────────
 *
 * It said this "matches `.nodeRowDeemphasised` in the stylesheet (0.45) so a losing discount
 * rate reads the same in both views". That parity is gone: the TABLE view no longer renders a
 * row scoped to another reserve category at all, because its heading is "Inputs used to value
 * slot X" and such a row was never an input to this slot.
 *
 * THE MAP IS ANSWERING A DIFFERENT QUESTION and keeps the fade. It draws the whole registry —
 * every category's rates and all four schedule functions — because it is a picture of the
 * MODEL rather than a list of one slot's inputs, and a model with nine rates silently missing
 * would misrepresent the graph. Faded is the right answer here and absent is the right answer
 * there; they are not inconsistent, they are two questions.
 */
export const FADED_NODE_OPACITY = 0.45;

/**
 * AC 5 renders formula text ON THE NODE, and the registry's formulas are large.
 *
 * MEASURED across the 143 drawn nodes: 28 carry a formula, 17 of those are multi-line, and
 * the median longest line is 70 characters — so at a 150px node width essentially every
 * multi-line formula wraps. The extremes are `afe_cost` (10 lines, 583 characters, longest
 * line 76), `irr` (a single 196-character line) and `payout_month` (175).
 *
 * That is a real cost and it is accepted rather than hidden. AC 8's own wording — "formula
 * text must never WIDEN A NODE beyond the standard node width" — only means anything if the
 * text is on the node, so the spec already answered where it goes. Width stays bounded by
 * `WrapFit` against {@link NODE_MAX_WIDTH}; the extra height is absorbed by the layout,
 * which positions from measured bounds and therefore cannot overlap.
 */

/** One AC 6 line: an input node's value, rendered under the function node it feeds. */
export interface ModelMapValueLine {
  /** The INPUT node this line reports — NOT the function node it renders under. */
  key: NodeKey;
  text: string;
}

/**
 * The per-node datum the diagram binds and hands back on selection.
 *
 * ── THE THREE DE-EMPHASIS STATES ARE THREE SEPARATE FIELDS, DELIBERATELY ──────────────
 *
 * `dimmed`     transient. A click dims everything outside the clicked node's ancestry; a
 *              background click clears it. Owned by GoJS at runtime.
 * `faded`      permanent for the life of the diagram. AC 4's losing option group — a rate
 *              that IS applied and DOES have a value, for another reserve category.
 * `unresolved` permanent. The node produced no value at all.
 *
 * They must not share a field. If category fading were expressed as `dimmed`, clearing a
 * click-highlight would un-fade the nine losing rates — the highlight's reset writes
 * `dimmed: false` across every node unconditionally. And collapsing `faded` into
 * `unresolved` would tell a reader a rate is MISSING when it is present and simply not
 * selected, which is the opposite of what the tearsheet exists to say.
 *
 * `faded` and `unresolved` therefore render on DIFFERENT CHANNELS: opacity for the first,
 * stroke dash for the second, so no combination of the three is ambiguous.
 */
export interface ModelMapNodeDatum {
  key: NodeKey;
  label: string;
  kind: NodeKind;
  zoneLabel: string;
  formula: string | null;
  inputs: NodeKey[];
  /** Layout/highlight only — GoJS toggles this to dim non-ancestors. TRANSIENT. */
  dimmed: boolean;
  /**
   * AC 5: the formula to display, or null on a node that has none.
   *
   * VERBATIM when present — never reformatted, never trimmed into a different string —
   * because the table renders `SlotInputNode.formula` raw and AC 14 forbids the two views
   * showing different text for the same node. Only the PRESENCE decision looks at
   * whitespace. See {@link displayFormula}.
   */
  formulaText: string | null;
  /** AC 6: the formatted values of the named inputs feeding this node. Function nodes only. */
  valueLines: ModelMapValueLine[];
  /** AC 4: this node is a rate belonging to ANOTHER reserve category. */
  faded: boolean;
  /**
   * This node is a SOURCE PARENT — it names where a group of values came from and is drawn as
   * their parent (Volume Source over the gross streams, AFE over its costs). Rendered as a
   * purple box, matching the scenario parents in the mock. See {@link SOURCE_PARENTS}.
   */
  isSourceParent: boolean;
  /**
   * This node was SUPPOSED to carry a value and does not.
   *
   * NEVER true on a function node. A function is a derivation — it has no value of its own
   * and can never be `applied` — so marking it "no value" says something false about all
   * 24 of them, which the first real render made obvious: every interesting box on the map
   * was dashed. That is the same conflation spec 5.4 is about, one view over.
   *
   * False when there is no slot data at all, so the map fails open.
   */
  unresolved: boolean;
}

/** One drawn edge. `dimmed` is the same transient highlight state as on a node. */
export interface ModelMapLinkDatum {
  key: string;
  from: NodeKey;
  to: NodeKey;
  dimmed: boolean;
}

/**
 * ── THE SYNTHESIZED SCENARIO PARENTS ─────────────────────────────────────────────────────
 *
 * Every scenario domain gets its picked scenario shown as the purple parent of its pieces,
 * just like Volume Source and AFE — "Development Pace Scenario over the pace pieces". These are
 * ADDITIVE, drawn on the map's existing layout exactly as the two real source parents are; they
 * do not change the design. The scenario is provenance metadata, not a registry node, so the
 * parent is synthesized here from the payload's `domains`.
 *
 * ── WHAT A PARENT FEEDS ──────────────────────────────────────────────────────────────────
 *
 * Its domain's ROOTS — nodes with no in-domain registry parent — EXCEPT any piece already
 * parented by a real source node (the AFE costs, so the Expense Scenario does not double up on
 * them) and the source-parent nodes themselves. A domain with no drawn root, or whose zone is
 * not slot-scoped (royalty_revenue), gets no parent rather than one floating loose.
 *
 * The label carries the scenario NAME when the run recorded one; on a platform-default run
 * (`scenarioName` null, the common case) it reads "<Domain> Scenario".
 *
 * @param {DomainGroup[]} domains - The payload's per-domain groups (nodeKeys, provenance).
 * @param {Set<NodeKey>} drawnKeys - The keys the diagram draws.
 * @param {ModelMapResponse} response - For in-domain edges and zone slot-scoping.
 * @param {Set<NodeKey>} alreadyParented - Pieces a real source parent already feeds.
 * @return {object} `{ nodes, links }` — the synthetic parents and their edges.
 */
export function buildScenarioParents(
  domains: DomainGroup[],
  drawnKeys: Set<NodeKey>,
  response: ModelMapResponse,
  alreadyParented: Set<NodeKey>
): { nodes: ModelMapNodeDatum[]; links: ModelMapLinkDatum[] } {
  const nodes: ModelMapNodeDatum[] = [];
  const links: ModelMapLinkDatum[] = [];
  for (const domain of domains) {
    if (response.zones[domain.zone]?.slotScoped === false) {
      continue;
    }
    const members = new Set(
      domain.nodeKeys.filter((key) => drawnKeys.has(key))
    );
    if (members.size === 0) {
      continue;
    }
    const hasInDomainParent = new Set<NodeKey>();
    for (const [from, to] of response.edges) {
      if (members.has(from) && members.has(to)) {
        hasInDomainParent.add(to);
      }
    }
    const roots = [...members].filter(
      (key) =>
        !hasInDomainParent.has(key) &&
        !alreadyParented.has(key) &&
        !isSourceParentKey(key)
    );
    if (roots.length === 0) {
      continue;
    }
    const key = `scenario:${domain.domain}`;
    const name = domain.provenance?.scenarioName;
    nodes.push({
      key,
      label: name ? `${domain.label}\n${name}` : `${domain.label} Scenario`,
      kind: 'scenario_config',
      zoneLabel: domain.label,
      formula: null,
      inputs: [],
      dimmed: false,
      formulaText: null,
      valueLines: [],
      faded: false,
      isSourceParent: true,
      unresolved: false,
    });
    for (const root of roots) {
      links.push({
        key: `scen-${domain.domain}-${root}`,
        from: key,
        to: root,
        dimmed: false,
      });
    }
  }
  return { nodes, links };
}

/**
 * Whether a node of this kind is expected to carry a value at all.
 *
 * ── DELIBERATELY NOT `node_binder.rb`'s VALUE_KINDS, AND THE DIFFERENCE MATTERS ───────
 *
 * That constant is `[:input, :scenario_config]` and its own comment scopes it: "kinds that
 * carry a VALUE ... resolvedInputCount counts these". Reaching for it here would look like
 * good hygiene and be wrong, because it excludes two kinds that genuinely do resolve and
 * genuinely can fail to:
 *
 *   output     32 of 35 resolve on EC 61's DUC slot. `completion_date` is deliberately
 *              unbound (it is persisted nowhere), and a reader should see that.
 *   parameter  `pace_interval` and `ltd_rank` are computed at scheduling and persisted
 *              nowhere — exactly the blank inputs the deviations page documents. Marking
 *              them is the point, not noise.
 *
 * Only `function` is excluded, because a derivation has no value to be missing.
 *
 * @param {NodeKind} kind - The node's kind.
 * @return {boolean} False only for a function node.
 */
export function canCarryValue(kind: NodeKind): boolean {
  return kind !== 'function';
}

/**
 * AC 5 — the formula a node should display, or null when it must display none.
 *
 * ── "PROSE-ONLY DEFINITIONS RENDER NO FORMULA" IS A REAL CASE, NOT A HYPOTHETICAL ─────
 *
 * Measured 25 Sep 2026 against the committed fixture: 112 of the 216 drawn nodes carry prose
 * with no formula, and ONE of them is a FUNCTION node — `pdp_schedule`. (It read "48 of the
 * 143 drawn nodes ... three of them FUNCTION nodes" and both halves had gone stale: the set
 * dropped to one when `type_curve_blending` and `fractionalization` had
 * formulas derived for them from the calculator, and those two have since been removed from
 * the registry altogether — see model_map.rb, above `netting`.) One is enough to make the
 * case: returning null rather than an empty string is what lets the caller omit the element
 * entirely instead of drawing an empty box where a formula would be, which is the failure
 * AC 5 names, and it is a failure at a count of one.
 *
 * The value is returned VERBATIM. Trimming decides only whether anything is there: a
 * trimmed string would be a SECOND rendering of the same formula, differing from the
 * table's by whitespace, and AC 14 forbids exactly that. (No formula in the registry
 * currently has surrounding whitespace or is the empty string — checked — so this guard
 * costs nothing today and is here for the one that eventually does.)
 *
 * @param {(string|null)} [formula] - The node's declared formula.
 * @return {(string|null)} The formula to render, or null to render none.
 */
export function displayFormula(
  formula: string | null | undefined
): string | null {
  return formula && formula.trim().length > 0 ? formula : null;
}

/**
 * AC 6 — "function nodes list the actual values of the named inputs feeding them, capped at
 * four lines, omitting empty, multi-line, or overlong values".
 *
 * Only FUNCTION nodes get lines; every other kind returns empty. The values are formatted
 * through {@link formatNodeDisplay}, the same entry point the table uses, so a discount rate
 * that reads "10%" in the table cannot read "0.1" on the map, nor a slot length read
 * "13,937" in the table and "13,936.50758174" here. `formatNodeValue` (underneath it) is the one
 * that routes `fraction` / `percent` / `percent_per_year` through `formatPercentScale` —
 * calling that helper directly here would be the second formatting path this epic exists to
 * prevent, one file over.
 *
 * The three omission rules, in the order AC 6 names them:
 *
 *   empty       the input did not resolve, carries no entries, or formats to the empty
 *               indicator. `0.0` and `false` are REAL values and are kept — the payload's
 *               `applied` flag is what decides, never truthiness of the value.
 *   multi-line  a monthly node (it has a `seriesKey`) is a SERIES, not a value: its four-point
 *               preview would spend the whole line budget on one input. A formatted string
 *               containing a newline is dropped for the same reason — registry formulas are
 *               genuinely multi-line (`ltd_schedule` carries four `\n`-separated lines).
 *   overlong    past {@link MAX_VALUE_LINE_CHARS}, where wrapping would grow the node's
 *               height and break AC 8.
 *
 * @param {NodeKind} kind - The node's kind. Anything but `function` yields no lines.
 * @param {NodeKey[]} inputs - The node's declared input keys, in registry order.
 * @param {Map} resolved - The slot's resolved inputs, keyed by node key.
 * @param {object} nodes - The registry nodes, for each input's declared unit.
 * @return {ModelMapValueLine[]} At most {@link MAX_VALUE_LINES} lines.
 */
export function valueLinesFor(
  kind: NodeKind,
  inputs: NodeKey[],
  resolved: Map<NodeKey, SlotInputNode>,
  nodes: ModelMapResponse['nodes']
): ModelMapValueLine[] {
  if (kind !== 'function') {
    return [];
  }
  const lines: ModelMapValueLine[] = [];
  for (const inputKey of inputs) {
    if (lines.length >= MAX_VALUE_LINES) {
      break;
    }
    const input = resolved.get(inputKey);
    if (!input || !input.applied || input.values.length === 0) {
      continue;
    }
    if (input.seriesKey) {
      continue;
    }
    const formatted = formatNodeDisplay(
      inputKey,
      input.values[0].value,
      nodes[inputKey]?.unit
    );
    if (formatted === EMPTY) {
      continue;
    }
    const text = `${input.label}: ${formatted}`;
    if (text.includes('\n') || text.length > MAX_VALUE_LINE_CHARS) {
      continue;
    }
    lines.push({ key: inputKey, text });
  }
  return lines;
}

/**
 * Node opacity from the two PERMANENT-vs-TRANSIENT states, composed so they cannot fight.
 *
 * A live click-highlight wins while it lasts; clearing it drops the node back to its
 * category fading rather than to full opacity. That ordering is the whole point — the
 * alternative loses AC 4's fading the first time anyone clicks a node.
 *
 * @param {object} datum - The node datum's `dimmed` and `faded` flags.
 * @return {number} The opacity to bind.
 */
export function nodeOpacity(
  datum: Pick<ModelMapNodeDatum, 'dimmed' | 'faded'>
): number {
  if (datum.dimmed) {
    return DIMMED_NODE_OPACITY;
  }
  if (datum.faded) {
    return FADED_NODE_OPACITY;
  }
  return 1;
}

/**
 * Builds the diagram's node and link data from the registry response and — when the
 * tearsheet payload is available — the slot's resolved values.
 *
 * Zones flagged `slotScoped: false` are NOT drawn (Type Curve and Royalty/Revenue), and any
 * edge touching a dropped node is pruned along with self-loops the layout cannot route.
 *
 * ── EVERY SLOT-DEPENDENT FIELD FAILS OPEN ─────────────────────────────────────────────
 *
 * `slotInputs` is optional because the map's own request can land before the tearsheet's,
 * and because the map is reachable without one. With no slot data the diagram renders
 * exactly as it did before AC 4 and AC 6 existed: no value lines, nothing faded, nothing
 * marked unresolved. `unresolved` in particular is gated on there being slot data at all —
 * without it, every one of the 221 nodes would claim to have no value, which is the loudest
 * possible way to be wrong.
 *
 * @param {ModelMapResponse} response - The registry map (zones / nodes / edges).
 * @param {SlotInputNode[]} [slotInputs] - The slot's resolved inputs, when loaded.
 * @param {(string|null)} [slotCategory] - The slot's reserve category, when resolved.
 * @param {DomainGroup[]} [domains] - The payload's per-domain groups, for the scenario parents.
 * @return {object} `{ nodeData, linkData, scenarioNodes, scenarioLinks }` for the GoJS model.
 */
export function buildModelMapData(
  response: ModelMapResponse,
  slotInputs: SlotInputNode[] = [],
  slotCategory: string | null = null,
  domains: DomainGroup[] = []
): {
  nodeData: ModelMapNodeDatum[];
  linkData: ModelMapLinkDatum[];
  scenarioNodes: ModelMapNodeDatum[];
  scenarioLinks: ModelMapLinkDatum[];
} {
  const resolved = new Map<NodeKey, SlotInputNode>(
    slotInputs.map((node) => [node.key, node])
  );
  const haveSlotData = resolved.size > 0;

  const drawnNodeKeys = new Set<NodeKey>();
  const nodeData: ModelMapNodeDatum[] = [];
  for (const [key, node] of Object.entries(response.nodes)) {
    const zone = response.zones[node.zone];
    if (zone && zone.slotScoped === false) {
      continue;
    }
    drawnNodeKeys.add(key);
    nodeData.push({
      key,
      label: isSourceParentKey(key)
        ? sourceParentLabel(node.label, resolved.get(key), node.unit)
        : node.label,
      kind: node.kind,
      zoneLabel: zone?.label ?? node.zone,
      formula: node.formula,
      inputs: node.inputs,
      dimmed: false,
      formulaText: displayFormula(node.formula),
      valueLines: valueLinesFor(
        node.kind,
        node.inputs,
        resolved,
        response.nodes
      ),
      faded: isUnappliedCategoryRate(node.category, slotCategory),
      unresolved: canCarryValue(node.kind)
        ? haveSlotData && !(resolved.get(key)?.applied ?? false)
        : false,
      isSourceParent: isSourceParentKey(key),
    });
  }

  const linkData = response.edges
    .filter(
      ([from, to]) =>
        from !== to && drawnNodeKeys.has(from) && drawnNodeKeys.has(to)
    )
    .map(([from, to], i) => ({ key: `lk-${i}`, from, to, dimmed: false }));

  const srcLinks = sourceParentLinks(drawnNodeKeys);
  const alreadyParented = new Set(srcLinks.map((link) => link.to));
  // buildScenarioParents already skips non-slot-scoped domains (royalty_revenue) and any domain
  // with no drawn root, so its nodes and links need no further filtering here.
  const scenario = buildScenarioParents(
    domains,
    drawnNodeKeys,
    response,
    alreadyParented
  );

  return {
    nodeData,
    linkData: [...linkData, ...srcLinks],
    scenarioNodes: scenario.nodes,
    scenarioLinks: scenario.links,
  };
}

/**
 * ── SOURCE PARENTS: "this is where these pieces came from" ────────────────────────────────
 *
 * Andrew's ask: the source that was picked sits up top and breaks out into all the pieces it
 * contributes — "AFE 691 is up here, and it breaks out into all these pieces". Each key is a
 * node that NAMES a source (and carries its value/id); its values are the pieces it feeds.
 *
 * These are the two domains that have a REAL registry node naming their source:
 *   Volume Source  `slot_volume_source` → the gross streams.
 *   AFE            `afe_well_name` (e.g. "AFE 691") → its cost pieces.
 *
 * The scenario domains (Development Pace, Expenses, Price Deck, Tax, Discount) have no such
 * node — their "picked scenario" is provenance metadata — so they are handled separately.
 */
const SOURCE_PARENTS: Record<NodeKey, NodeKey[]> = {
  slot_volume_source: ['gross_oil', 'gross_gas'],
  afe_well_name: [
    'afe_drilling_costs',
    'afe_completion_costs',
    'afe_facilities_costs',
    'afe_contingencies_costs',
    'afe_other_costs',
  ],
};

/**
 * Whether a node is one of the source parents drawn as a purple box. See {@link SOURCE_PARENTS}.
 *
 * @param {NodeKey} key - The node key to test.
 * @return {boolean} True when the node names a source and is drawn as its pieces' parent.
 */
export function isSourceParentKey(key: NodeKey): boolean {
  return Object.prototype.hasOwnProperty.call(SOURCE_PARENTS, key);
}

/**
 * The label a source parent shows: its registry label, then its RESOLVED VALUE on a second
 * line — the actual AFE name ("AFE 691"), the actual volume-source id. Andrew: "AFE 691 is up
 * here", so the box has to carry the id the reader recognises, not just the word "AFE Name".
 *
 * Fails open: with no slot data, or an unresolved/empty value (a DUC slot with no AFE), it
 * shows the label alone rather than a blank second line.
 *
 * @param {string} label - The node's registry label.
 * @param {(SlotInputNode|undefined)} resolved - The slot's resolution of this node, if any.
 * @param {NodeUnit} [unit] - The node's declared unit, to format the value.
 * @return {string} `"AFE Name\nAFE 691"`, or the bare label.
 */
function sourceParentLabel(
  label: string,
  resolved: SlotInputNode | undefined,
  unit?: NodeUnit
): string {
  const value = resolved?.applied ? resolved.values?.[0]?.value : undefined;
  if (value === undefined || value === null) {
    return label;
  }
  const formatted = formatNodeValue(value, unit);
  return formatted === EMPTY ? label : `${label}\n${formatted}`;
}

/**
 * The source parents drawn as the PARENTS of the pieces they contribute, so the map shows
 * where those pieces came from.
 *
 * ── PRESENTATION EDGES, NOT REGISTRY EDGES ───────────────────────────────────────────────
 *
 * `slot_volume_source` and `afe_well_name` are `EDGE_FREE_BY_DESIGN` in the registry — they
 * NAME a source without being a computed input (model_map.rb), which is why they draw isolated
 * today. That is right for the registry, the honest picture of the CALCULATION. But the ask is
 * lineage — where did these pieces come from — so the map draws the connection a reader needs.
 * VIEW-ONLY: the registry, its edges and its invariant specs are untouched.
 *
 * Emitted only for nodes actually drawn, so a payload missing one gets fewer links, never a
 * dangling edge.
 *
 * @param {Set<NodeKey>} drawnKeys - The keys the diagram is drawing.
 * @return {ModelMapLinkDatum[]} The source-parent → piece links, or none.
 */
function sourceParentLinks(drawnKeys: Set<NodeKey>): ModelMapLinkDatum[] {
  const links: ModelMapLinkDatum[] = [];
  for (const [source, pieces] of Object.entries(SOURCE_PARENTS)) {
    if (!drawnKeys.has(source)) {
      continue;
    }
    for (const piece of pieces) {
      if (drawnKeys.has(piece)) {
        links.push({
          key: `src-${source}-${piece}`,
          from: source,
          to: piece,
          dimmed: false,
        });
      }
    }
  }
  return links;
}
